import * as A from 'fp-ts-esm/Array';
import { flow, pipe } from 'fp-ts-esm/function';
import * as R from 'fp-ts-esm/Record';
import * as T from 'fp-ts-esm/Tuple';

import { fromEntries } from './../ricos-content/lib/utils.js';
import type { DocumentStyle, TextNodeStyle } from './../ricos-schema/legacy/wix/rich_content/v1/index.js';
import type { CustomTextualStyle, RicosCustomStyles, RicosTheme } from './../ricos-types/index.js';

import { Decorations } from './decorations/index.js';
import { RicosNodeStyle } from './document-style/node-style.js';
import { RicosTextStyle } from './document-style/text-style.js';
import type { TextBlockNodeType } from './models/styles.js';
import CustomStyles from './textual-theme/custom-styles.js';

type DocumentStyleTuple = [type: TextBlockNodeType, styles: TextNodeStyle];
type CustomStyleKey = keyof RicosCustomStyles;

const documentToThemeKeyMap: Record<TextBlockNodeType, CustomStyleKey> = {
  headerOne: 'h1',
  headerTwo: 'h2',
  headerThree: 'h3',
  headerFour: 'h4',
  headerFive: 'h5',
  headerSix: 'h6',
  paragraph: 'p',
  blockquote: 'quote',
  codeBlock: 'codeBlock',
};

const themeToDocumentKeyMap: Record<CustomStyleKey, TextBlockNodeType> = pipe(
  documentToThemeKeyMap,
  Object.entries,
  A.map(T.swap),
  fromEntries
);

const toCustomStyle = (node: TextNodeStyle): CustomTextualStyle => ({
  ...Decorations.of(node?.decorations).toCustomStyle(),
  ...RicosNodeStyle.of(node?.nodeStyle).toCustomStyle(),
  ...RicosTextStyle.of({ lineHeight: node?.lineHeight }).toCustomStyle(),
});

const toCustomStyleKey = (nodeType: TextBlockNodeType): CustomStyleKey =>
  documentToThemeKeyMap[nodeType] as CustomStyleKey;

const toTuples = (documentStyle: Record<TextBlockNodeType, TextNodeStyle>): DocumentStyleTuple[] =>
  R.toArray(documentStyle);

const fromTuples = (tuple: [key: CustomStyleKey, value: CustomTextualStyle][]): RicosCustomStyles =>
  fromEntries(tuple);

// @ts-expect-error $TSFixMe
const toRicosCustomStyles: (documentStyle: DocumentStyle) => RicosCustomStyles = flow(
  toTuples,
  A.map(T.bimap(toCustomStyle, toCustomStyleKey)),
  fromTuples
);

const toTextNodeStyle = (customStyle: CustomTextualStyle): TextNodeStyle => ({
  decorations: Decorations.fromCustomStyle(customStyle).toDecorationArray(),
  nodeStyle: RicosNodeStyle.fromCustomStyle(customStyle).getNodeStyle(),
  lineHeight: RicosTextStyle.fromCustomStyle(customStyle).getTextStyle().lineHeight,
});

const toTextNodeType = (customStyleKey: CustomStyleKey): TextBlockNodeType =>
  themeToDocumentKeyMap[customStyleKey];

const toDocumentStyle: (customStyle: RicosCustomStyles) => DocumentStyle = flow(
  // @ts-expect-error $TSFixMe
  Object.entries,
  A.map(T.bimap(toTextNodeStyle, toTextNodeType)),
  fromEntries,
  JSON.stringify,
  JSON.parse
);

export class TextStyleTransformer {
  private theme: RicosTheme;

  private constructor(theme: RicosTheme) {
    this.theme = theme;
  }

  static fromTheme(theme: RicosTheme) {
    return new TextStyleTransformer(theme || {});
  }

  static fromDocumentStyle(documentStyle: DocumentStyle) {
    const customStyles = toRicosCustomStyles(documentStyle);
    return new TextStyleTransformer({ customStyles });
  }

  toTheme() {
    return this.theme;
  }

  toDocumentStyle() {
    return toDocumentStyle(CustomStyles.fromTheme(this.theme).toCustomStyles());
  }
}
